export type HeaderMap = Record<string, string>;

type FetchJsonOptions = RequestInit & {
  headers?: HeaderMap | Headers | null;
};

type FetchJsonMethod = (
  url: string,
  body?: any,
  options?: FetchJsonOptions
) => Promise<any>;

export type FetchJsonMethodName =
  | 'get'
  | 'post'
  | 'put'
  | 'patch'
  | 'delete'
  | 'head';

type FetchJsonClient = FetchJsonMethod &
  Record<FetchJsonMethodName, FetchJsonMethod> & {
    headers(): HeaderMap;
    headers(headers: HeaderMap): FetchJsonClient;
    clearHeaders: () => FetchJsonClient;
    version: string;
  };

const defaultHeaders: HeaderMap = {
  'content-type': 'application/json',
  accept: 'application/json',
};

let sharedHeaders: HeaderMap = { ...defaultHeaders };

const headerEntries = (
  headers?: HeaderMap | Headers | null
): Array<[string, string]> => {
  if (!headers) {
    return [];
  }
  if (headers instanceof Headers) {
    return Array.from(headers.entries());
  }
  return Object.entries(headers);
};

const normalizeHeaders = (headers?: HeaderMap | Headers | null): HeaderMap =>
  headerEntries(headers).reduce((result: HeaderMap, [key, value]) => {
    if (value) {
      result[key.toLowerCase()] = value;
    }
    return result;
  }, {});

const parseJsonResponse = (response: Response): Promise<any> =>
  response.json().then(
    (data) => {
      if (response.ok) {
        return data;
      }
      return Promise.reject({
        status: response.status,
        statusText: response.statusText,
        response: data,
      });
    },
    (error) => {
      if (response.ok) {
        return {};
      }
      return Promise.reject({
        status: response.status,
        statusText: response.statusText,
        error,
      });
    }
  );

const request = (url: string, options: FetchJsonOptions = {}): Promise<any> => {
  const config: FetchJsonOptions = {
    ...options,
    method: (options.method || 'GET').toUpperCase(),
  };
  const hasFormDataBody =
    typeof FormData !== 'undefined' && config.body instanceof FormData;

  if (config.method === 'GET' || config.method === 'HEAD') {
    delete config.body;
  } else if (config.body && !hasFormDataBody) {
    config.body = JSON.stringify(config.body);
  }

  config.headers = {
    ...sharedHeaders,
    ...normalizeHeaders(config.headers),
  };
  if (hasFormDataBody) {
    delete (config.headers as HeaderMap)['content-type'];
  }

  return fetch(url, config).then(parseJsonResponse);
};

const fetchJson = request as FetchJsonClient;

(['get', 'post', 'put', 'patch', 'delete', 'head'] as const).forEach(
  (method) => {
    fetchJson[method] = (
      url: string,
      body?: any,
      options: FetchJsonOptions = {}
    ): Promise<any> => {
      const config = { ...options, method };

      if ((method === 'get' || method === 'head') && body) {
        return request(url, { ...body, method });
      }

      if (body) {
        config.body = body;
      }

      return request(url, config);
    };
  }
);

function headers(): HeaderMap;
function headers(headers: HeaderMap): FetchJsonClient;
function headers(headers?: HeaderMap): HeaderMap | FetchJsonClient {
  if (!headers) {
    return sharedHeaders;
  }

  sharedHeaders = {
    ...defaultHeaders,
    ...normalizeHeaders(headers),
  };

  return fetchJson;
}

fetchJson.headers = headers;

fetchJson.clearHeaders = (): FetchJsonClient => {
  sharedHeaders = {};
  return fetchJson;
};

fetchJson.version = 'local';

export default fetchJson;
